import { Link } from "react-router-dom";
import { MapPin, Phone, Mail, Clock, Bot, Shield, Star, Award, CheckCircle2 } from "lucide-react";
import logoImage from "@/assets/lifetime-movers-logo-transparent.png";

export const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer id="contact" className="bg-primary text-primary-foreground">
      {/* Trust Signals Bar */}
      <div className="bg-background border-t border-b border-border py-6">
        <div className="container mx-auto px-4">
          <div className="flex flex-wrap items-center justify-center gap-6 md:gap-10">
            {/* Google Rating */}
            <div className="flex items-center gap-2">
              <div className="flex items-center" aria-hidden="true">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="h-4 w-4 text-secondary fill-secondary" />
                ))}
              </div>
              <div className="text-left">
                <span className="font-bold text-foreground">4.9</span>
                <span className="text-xs text-muted-foreground ml-1">5,000+ Reviews</span>
              </div>
            </div>
            
            <div className="hidden md:block w-px h-8 bg-border" />
            
            <div className="flex items-center gap-2 text-foreground">
              <Shield className="h-5 w-5 text-primary" />
              <span className="text-sm font-medium">Licensed & Insured</span>
            </div>
            
            <div className="hidden md:block w-px h-8 bg-border" />
            
            <a href="https://www.homemovingnow.com" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-foreground hover:opacity-80 transition-opacity">
              <img src="/images/homemovingnow-logo.png" alt="HomeMovingNow Listed" className="h-[24px] w-auto" />
              <span className="text-sm font-medium">HomeMovingNow Listed</span>
            </a>
            
            <div className="hidden md:block w-px h-8 bg-border" />
            
            <div className="flex items-center gap-2 text-foreground">
              <CheckCircle2 className="h-5 w-5 text-secondary" />
              <span className="text-sm font-medium">98% Satisfaction Rate</span>
            </div>
            
            <div className="hidden md:block w-px h-8 bg-border" />
            
            <div className="flex items-center gap-2 text-foreground">
              <Shield className="h-5 w-5 text-primary" />
              <span className="text-sm font-medium">USDOT 3733668 | MC 1318959</span>
            </div>
            
            <div className="hidden md:block w-px h-8 bg-border" />
            
            <div className="flex items-center gap-2 text-foreground">
              <Clock className="h-5 w-5 text-secondary" />
              <span className="text-sm font-medium">Est. 2018 • 7+ Years</span>
            </div>
          </div>
        </div>
      </div>
      
      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* Company Info */}
          <div>
            <img 
              src={logoImage} 
              alt="LifeTime Movers" 
              className="h-24 w-auto mb-4"
            />
            <p className="text-primary-foreground/80 mb-4">
              LifeTime Movers (Lifetime Moving) — professional moving services from Chicago. Local moves within Illinois and interstate moves from IL, IN, MI, WI to anywhere in the continental U.S. Licensed, insured, and trusted by thousands.
            </p>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-lg font-semibold mb-4">Contact Us</h4>
            <div className="space-y-3">
              <a href="tel:+16306869987" className="flex items-start gap-2 text-primary-foreground/80 hover:text-secondary transition-colors">
                <Phone className="h-5 w-5 mt-0.5 flex-shrink-0" />
                <span>(630) 686-9987</span>
              </a>
              <a href="mailto:info@lifetimemovers.us" className="flex items-start gap-2 text-primary-foreground/80 hover:text-secondary transition-colors">
                <Mail className="h-5 w-5 mt-0.5 flex-shrink-0" />
                <span>info@lifetimemovers.us</span>
              </a>
              <div className="flex items-start gap-2 text-primary-foreground/80">
                <MapPin className="h-5 w-5 mt-0.5 flex-shrink-0" />
              <span>
                2021 Midwest Rd STE 200<br />
                Oak Brook, IL 60523<br />
                Local Chicago Movers, and<br />
                IL, IN, MI, WI to Anywhere in the USA.
                </span>
              </div>
              <Link to="/lifetime-movers-reviews" className="flex items-start gap-2 text-primary-foreground/80 hover:text-secondary transition-colors">
                <Star className="h-5 w-5 mt-0.5 flex-shrink-0" />
                <span>LifeTime Reviews</span>
              </Link>
            </div>
          </div>

          {/* Business Hours & Vibe Assistant */}
          <div>
            <h4 className="text-lg font-semibold mb-4">Business Hours</h4>
            <div className="space-y-3">
              <div className="flex items-start gap-2 text-primary-foreground/80">
                <Clock className="h-5 w-5 mt-0.5 flex-shrink-0" />
                <div>
                  <p>Monday - Friday: 7am - 8pm</p>
                  <p>Saturday: 8am - 6pm</p>
                  <p>Sunday: 9am - 5pm</p>
                </div>
              </div>
            </div>

            {/* Vibe 24/7 Assistant */}
            <div className="mt-4">
              <h4 className="text-lg font-semibold mb-4 text-secondary">24/7 Vibe Assistant</h4>
              <div className="flex items-start gap-2 text-primary-foreground/80">
                <Bot className="h-5 w-5 mt-0.5 flex-shrink-0 text-secondary" />
                <div>
                  <p>Our AI assistant can quote, book, and schedule your move anytime, day or night.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-lg font-semibold mb-4">Quick Links</h4>
            <nav aria-label="Quick links" className="space-y-2">
              <Link to="/" className="block text-primary-foreground/80 hover:text-secondary transition-colors">
                Home
              </Link>
              <Link to="/moving-services" className="block text-primary-foreground/80 hover:text-secondary transition-colors">
                Moving Services
              </Link>
              <Link to="/service-areas" className="block text-primary-foreground/80 hover:text-secondary transition-colors">
                Service Areas
              </Link>
              <Link to="/lifetime-movers-reviews" className="block text-primary-foreground/80 hover:text-secondary transition-colors">
                Reviews
              </Link>
              <Link to="/about-lifetime-movers" className="block text-primary-foreground/80 hover:text-secondary transition-colors">
                About LifeTime Movers
              </Link>
              <Link to="/lifetime-moving-company" className="block text-primary-foreground/80 hover:text-secondary transition-colors">
                LifeTime Moving Company
              </Link>
              <Link to="/lifetime-moving-and-storage" className="block text-primary-foreground/80 hover:text-secondary transition-colors">
                Moving & Storage
              </Link>
              <Link to="/blog" className="block text-primary-foreground/80 hover:text-secondary transition-colors">
                Blog
              </Link>
              <Link to="/blog/moving-companies-chicago-area-complete-guide" className="block text-primary-foreground/80 hover:text-secondary transition-colors">
                Chicago Area Moving Guide
              </Link>
              <Link to="/faq" className="block text-primary-foreground/80 hover:text-secondary transition-colors">
                FAQ
              </Link>
              <Link to="/contact" className="block text-primary-foreground/80 hover:text-secondary transition-colors">
                Contact
              </Link>
              <Link to="/quote" className="block text-primary-foreground/80 hover:text-secondary transition-colors">
                Get Estimate
              </Link>
              <Link to="/resume-booking" className="block text-primary-foreground/80 hover:text-secondary transition-colors">
                Resume Your Move
              </Link>
              <Link to="/auto-shipping-quotes" className="block text-primary-foreground/80 hover:text-secondary transition-colors">
                Auto Shipping Quotes
              </Link>
              <Link to="/former" className="block text-primary-foreground/90 hover:text-secondary transition-colors text-xs">
                Former Movers
              </Link>
            </nav>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-primary-foreground/30 mt-8 pt-8 text-primary-foreground/90">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p>&copy; 2018-{currentYear} LifeTime Movers. All rights reserved. Licensed & Insured.</p>
            <div className="flex flex-wrap gap-6">
              <Link to="/terms" className="hover:text-secondary transition-colors">
                Terms & Conditions
              </Link>
              <Link to="/privacy" className="hover:text-secondary transition-colors">
                Privacy Policy
              </Link>
              <Link to="/real-estate-brokers" className="hover:text-secondary transition-colors">
                Partner Program
              </Link>
              <Link to="/sitemap" className="hover:text-secondary transition-colors">
                Sitemap
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};
